import { StorageService } from '@/app/core/auth/storage.service'
import { environment } from '@/environments/environment'
import { inject, Injectable, NgZone } from '@angular/core'
import { Observable, Subject } from 'rxjs'

@Injectable({
  providedIn: 'root',
})
export class MercureService {
  private ngZone = inject(NgZone)
  private storageService = inject(StorageService)

  private eventSources = new Map<string, EventSource>()
  private messageSubject = new Subject<{ topic: string; data: unknown }>()

  public messages$: Observable<{ topic: string; data: unknown }> =
    this.messageSubject.asObservable()

  /**
   * Initialize Mercure session by setting the required authorization cookie.
   */
  public init() {
    const token = this.getMercureToken()
    console.log(
      `[Mercure] Initializing with token: ${token ? 'Found (len: ' + token.length + ')' : 'NOT FOUND'}`,
    )
    if (token) {
      const domain = environment.mercureCookieDomain
        ? `; domain=${environment.mercureCookieDomain}`
        : ''
      const secure = window.location.protocol === 'https:' ? '; Secure' : ''

      const cookieStr = `mercureAuthorization=${token}; path=/; SameSite=Lax${domain}${secure}`
      document.cookie = cookieStr
      console.log(
        `[Mercure] Cookie set: mercureAuthorization=[REDACTED]${domain}${secure}`,
      )
    } else {
      console.warn(
        '[Mercure] No token found during init. Private updates will not be received.',
      )
    }
  }

  /**
   * Subscribe to a specific topic for real-time updates.
   */
  public subscribe(topic: string) {
    if (this.eventSources.has(topic)) return

    const url = new URL(environment.mercureUrl)
    url.searchParams.append('topic', topic)

    this.ngZone.runOutsideAngular(() => {
      const es = new EventSource(url, { withCredentials: true })

      es.onopen = () => {
        console.log(`[Mercure] Connected to ${topic}`)
      }

      es.onerror = (err) => {
        console.error(
          `[Mercure] Connection error for ${topic}. EventSource will retry automatically.`,
          err,
        )
      }

      const handler = (event: MessageEvent) => {
        console.log(`[Mercure Debug] Raw event received on topic ${topic}:`, {
          data: event.data,
          lastEventId: event.lastEventId,
          origin: event.origin,
        })

        // Skip heartbeat (empty) messages
        if (!event.data) return

        this.ngZone.run(() => {
          try {
            const data = JSON.parse(event.data)
            console.log(`[Mercure Debug] Parsed data for ${topic}:`, data)
            this.messageSubject.next({ topic, data })
          } catch (e) {
            console.error(
              `[Mercure Debug] Failed to parse message for ${topic}. Raw:`,
              event.data,
              e,
            )
          }
        })
      }

      es.onmessage = handler
      es.addEventListener('message', handler)
      es.addEventListener('headox', handler)
      es.addEventListener('new', handler)
      es.addEventListener('typing', handler)
      // Listen to generic updates just in case
      es.addEventListener('update', handler)

      this.eventSources.set(topic, es)
    })
  }

  /**
   * Unsubscribe from a topic and close the EventSource.
   */
  public unsubscribe(topic: string) {
    const es = this.eventSources.get(topic)
    if (es) {
      es.close()
      this.eventSources.delete(topic)
    }
  }

  /**
   * Clear all active subscriptions.
   */
  public disconnect() {
    this.eventSources.forEach((es) => es.close())
    this.eventSources.clear()
  }

  private getMercureToken(): string {
    const token = this.storageService.getToken()
    if (!token) {
      console.warn('[Mercure Debug] No JWT token found in storage.')
      return ''
    }
    try {
      const payloadBase64 = token.split('.')[1]
      const payloadJson = atob(
        payloadBase64.replace(/-/g, '+').replace(/_/g, '/'),
      )
      const payload = JSON.parse(payloadJson)

      console.log(
        '[Mercure Debug] Decoded JWT payload keys:',
        Object.keys(payload),
      )
      const mToken = payload.mercureToken || payload.mercure?.token || ''

      if (!mToken) {
        console.warn(
          '[Mercure Debug] No mercureToken or mercure.token found in JWT payload. Keys available:',
          Object.keys(payload),
        )
      } else {
        console.log(
          `[Mercure Debug] Mercure token extracted successfully (prefix: ${mToken.substring(0, 10)}...)`,
        )
      }

      return mToken
    } catch (e) {
      console.error(
        '[Mercure Debug] Failed to decode JWT for Mercure token:',
        e,
      )
      return ''
    }
  }

  /**
   * Publish an event to Mercure hub.
   */
  public publish(
    topicOrTopics: string | string[],
    data: Record<string, unknown>,
    isPrivate = true,
    type?: string,
  ): Observable<void> {
    const token = this.getMercureToken()
    const payload = new URLSearchParams()

    const topics = Array.isArray(topicOrTopics)
      ? topicOrTopics
      : [topicOrTopics]
    topics.forEach((t) => payload.append('topic', t))

    payload.append('data', JSON.stringify(data))

    if (type) {
      payload.append('type', type)
    }

    if (isPrivate) {
      payload.append('private', 'on')
    }

    return new Observable((subscriber) => {
      fetch(environment.mercureUrl, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: payload,
        credentials: 'include',
      })
        .then((response) => {
          if (response.ok) {
            subscriber.next()
            subscriber.complete()
          } else {
            response.text().then((text) => {
              console.error(`[Mercure Debug] Publish failed body:`, text)
              subscriber.error(new Error(`Publish failed: ${text}`))
            })
          }
        })
        .catch((err) => {
          console.error(`[Mercure Debug] Publish fetch error:`, err)
          subscriber.error(err)
        })
    })
  }
}
